/**
 * @fileoverview The student portal's plus at phone width (C6, decided Nov 1,
 * 2026): Log practice, and Book a lesson with each teacher whose public
 * booking page is live. The web twin of mobile/src/components/StudentNewSheet.js;
 * on a wide screen the sidebar carries the same two.
 */

import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Icon } from "@/components/studio/primitives";
import { bookHint, bookLabel } from "@/lib/studentBooking";

const rowCls =
  "flex w-full items-center gap-[14px] border-b border-[var(--studio-divider)] px-[18px] py-[14px] text-left last:border-b-0 hover:bg-[var(--studio-raised)]";

function RowBody({ icon, title, hint }) {
  return (
    <>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)]">
        <Icon name={icon} size={19} className="text-[var(--accent-yellow-text)]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-sans text-base font-semibold text-[var(--text-primary)]">{title}</span>
        <span className="mt-0.5 block font-sans text-[13px] text-[var(--text-tertiary)]">{hint}</span>
      </span>
    </>
  );
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {boolean} props.canLog - Practice logs are on.
 * @param {() => void} props.onLog
 * @param {Array<Object>} props.bookingLinks
 */
export function StudentNewMenu({ open, onOpenChange, canLog, onLog, bookingLinks = [] }) {
  const many = bookingLinks.length > 1;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="studio rounded-t-[18px] border-[var(--studio-divider)] bg-[var(--studio-overlay)] p-0 pb-4"
        data-testid="student-new-menu"
      >
        <SheetTitle className="sr-only">New</SheetTitle>
        <SheetDescription className="sr-only">Log practice or book a lesson.</SheetDescription>
        <div className="mx-auto mb-1 mt-2 h-[5px] w-9 rounded-full bg-[var(--studio-divider-strong)]" aria-hidden="true" />
        {canLog && (
          <button
            type="button"
            className={rowCls}
            onClick={() => {
              onOpenChange(false);
              onLog();
            }}
            data-testid="student-new-log"
          >
            <RowBody icon="clock" title="Log practice" hint="How long, and what it was for" />
          </button>
        )}
        {bookingLinks.map((link) => (
          <a
            key={link.user_id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={rowCls}
            onClick={() => onOpenChange(false)}
            data-testid={`student-new-book-${link.user_id}`}
          >
            <RowBody icon="calendar" title={bookLabel(link, many)} hint={bookHint(link)} />
          </a>
        ))}
      </SheetContent>
    </Sheet>
  );
}

export default StudentNewMenu;
